import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  useAccountFeatures,
  useAccountSummary,
  useCreditAccounts
} from 'const/syfApiQueries'
import { useNavigate } from 'react-router-dom'
import config from 'const/config'
import handleAccountLink from 'helpers/handleAccountLink'
import routes from 'const/routes'
import findAccount from 'helpers/findAccount'
import useWindowSize from 'syf-component-library/ui/customHooks/useWindowWidth'
import handleRewardsPlatform from 'helpers/handleRewardsPlatform'
import queryClient from 'const/queryClient'
import clearSessionStorage from 'helpers/clearSessionStorage'
import handleRevokeAccessToken from 'helpers/handleRevokeAccessToken'
import redirectToUrl from 'helpers/redirectToUrl'
import handoverToPayAsGuest from 'helpers/handoverToPayAsGuest'
import handoverToRegisterAccount from 'helpers/handoverToRegisterAccount'
import getRewardsResponse from 'helpers/getRewardsResponse'
import useOpenStatementsMFE from 'hooks/useOpenStatementsMFE'
import { creditBalanceRefundModalContainerId } from 'const/modalContainerId'
import {
  modalStatusAtom,
  selectedAccountAtom,
  statementsFlowRefAtom
} from 'store/atoms'
import { useAtomValue, useSetAtom } from 'jotai'
import { MAPFlowTypes } from 'make-a-payment/lib/typings/deepLink'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import { setAnalyticsDefaults } from 'helpers/triggerAnalyticsEvent'
import { SAMS } from 'const/account/clients'

interface RewardsResponse {
  samlToken: string
}

const useGetCTAHandlers = (accountId: string) => {
  const navigate = useNavigate()
  const openStatementsMFE = useOpenStatementsMFE()
  const windowWidth = useWindowSize()

  const updateModalStatus = useSetAtom(modalStatusAtom)
  const setSelectedAccount = useSetAtom(selectedAccountAtom)
  const statementsFlowRef = useAtomValue(statementsFlowRefAtom)
  const { data: { accounts = [] } = {} } = useCreditAccounts()
  const { data: accountSummary } = useAccountSummary(accountId)
  const { data: accountFeatures } = useAccountFeatures(accountId)
  const account = findAccount(accounts, accountId)

  const { product } = accountSummary || {}
  const { type: productType, clientId = '', lineOfBusiness } = product || {}

  // Ref to store latest values to avoid stale closures
  const accountFeaturesRef = useRef<typeof accountFeatures>(accountFeatures)
  const accountRef = useRef<typeof account>(account)
  const clientIdRef = useRef<string>(clientId)
  const windowWidthRef = useRef<typeof windowWidth>(windowWidth)

  // Ref to queue handleRewardsClick if rewardDetails is not available
  const pendingRewardsClick = useRef<boolean>(false)

  // Update refs when values change
  useEffect(() => {
    accountFeaturesRef.current = accountFeatures
    accountRef.current = account
    clientIdRef.current = clientId
    windowWidthRef.current = windowWidth
  }, [accountFeatures, account, clientId, windowWidth])

  const activityButtonId = `activity-${accountId}`
  const paymentButtonId = `map-${accountId}`
  const collectionsButtonId = `collections-${accountId}`

  const samlTokenCacheKey = useMemo(
    () => ['saml-token', accountId],
    [accountId]
  )
  useQuery<RewardsResponse>(
    samlTokenCacheKey,
    () => getRewardsResponse(accountId),
    {
      enabled:
        Boolean(accountFeaturesRef.current?.rewardDetails?.ssoEnabled) &&
        Boolean(accountId) &&
        Boolean(
          accountFeaturesRef.current?.rewardDetails?.rewardsPlatformDestination
        )
    }
  )

  const handleAccountServicesClick = () => {
    setSelectedAccount(accountId)
    navigate(routes.ACCOUNT_SERVICES)
  }

  const handleStatementsClick = () => {
    setAnalyticsDefaults({ Product: productType, ClientName: clientId })
    setSelectedAccount(accountId)
    statementsFlowRef.current = 'accountcard_statements'

    openStatementsMFE()
  }

  const handleRewardsClick = useCallback(() => {
    // Always read fresh values from ref to avoid stale closures
    const currentAccountFeatures = accountFeaturesRef.current
    if (!currentAccountFeatures?.rewardDetails) {
      // Queue the click to be executed when rewardDetails becomes available
      pendingRewardsClick.current = true
      return
    }

    const { accessToRewardsMfe, ssoEnabled, rewardsPlatformDestination } =
      currentAccountFeatures.rewardDetails

    const samlToken =
      queryClient.getQueryState<RewardsResponse>(samlTokenCacheKey)?.data
        ?.samlToken

    const isRewardsError = Boolean(
      queryClient.getQueryState<RewardsResponse>(samlTokenCacheKey)?.error
    )

    const isSamsClient = clientIdRef.current === SAMS
    const isSamsRewardsMfeEnabled = isFeatureFlagEnabled(
      'isSamsRewardsMfeEnabled'
    )

    if (isSamsClient) {
      if (isSamsRewardsMfeEnabled && accessToRewardsMfe) {
        setSelectedAccount(accountId)
        navigate(routes.REWARDS)
      } else {
        handleAccountLink(
          accountRef.current,
          clientIdRef.current,
          windowWidthRef.current,
          'rewards'
        )
      }

      return
    }

    if (accessToRewardsMfe) {
      setSelectedAccount(accountId)
      navigate(routes.REWARDS)
      return
    }

    if (isRewardsError) {
      navigate(routes.REWARDS_ERROR)
      return
    }

    if (rewardsPlatformDestination && ssoEnabled) {
      handleRewardsPlatform(samlToken, rewardsPlatformDestination)
      queryClient.invalidateQueries(samlTokenCacheKey)
    } else if (rewardsPlatformDestination && !ssoEnabled) {
      window.open(rewardsPlatformDestination, '_blank', 'noopener,noreferrer')
    } else {
      handleAccountLink(
        accountRef.current,
        clientIdRef.current,
        windowWidthRef.current,
        'rewards'
      )
    }
  }, [accountId, navigate, samlTokenCacheKey, setSelectedAccount])

  // Effect: execute queued click when rewardDetails becomes available
  useEffect(() => {
    if (
      accountFeaturesRef.current?.rewardDetails &&
      pendingRewardsClick.current
    ) {
      pendingRewardsClick.current = false
      handleRewardsClick()
    }
  }, [accountFeatures?.rewardDetails, handleRewardsClick])

  const handleActivityClick = () => {
    setAnalyticsDefaults({ Product: productType, ClientName: clientId })
    updateModalStatus('Activity', activityButtonId)
    setSelectedAccount(accountId)
  }

  const handleDetailsClick = () => {
    setAnalyticsDefaults({ Product: productType, ClientName: clientId })
    setSelectedAccount(accountId)
    navigate(routes.ACCOUNT_DETAILS)
  }

  const handleCollectionClick = async () => {
    if (isFeatureFlagEnabled('isDigitalCollectionsExperienceEnabled')) {
      updateModalStatus('DigitalCollections', collectionsButtonId)
      setSelectedAccount(accountId)
    } else {
      const languageId = localStorage.getItem('language')
      await handleRevokeAccessToken()
      clearSessionStorage()
      redirectToUrl(
        `${config.DCH_HOST_NAME}/?account=${accountId}&lang=${languageId}`
      )
    }
  }

  const handleCBRClick = () => {
    setAnalyticsDefaults({
      Product: product?.type,
      ClientName: product?.clientId
    })
    setSelectedAccount(accountId)
    const manageCreditBalanceRefundButtonId =
      creditBalanceRefundModalContainerId(accountId)
    updateModalStatus('CreditBalanceRefund', manageCreditBalanceRefundButtonId)
  }

  const handleMAPClick = () => {
    setAnalyticsDefaults({
      Product: productType,
      ClientName: clientId,
      PageKind: 'payments'
    })

    setSelectedAccount(accountId)
    updateModalStatus('MAP', paymentButtonId)
  }

  const handleAutopayClick = () => {
    setAnalyticsDefaults({
      Product: productType,
      ClientName: clientId,
      PageKind: 'payments'
    })

    if (account.registration.registered) {
      setSelectedAccount(accountId)
      updateModalStatus('MAP', paymentButtonId, {
        MAP: { flow: { type: MAPFlowTypes.AUTOPAY_FLOW } }
      })
    } else {
      handoverToPayAsGuest(account.product, clientId)
    }
  }

  const handleRegisterAccountClick = () => {
    handoverToRegisterAccount(lineOfBusiness, clientId)
  }

  const handleAccountClick = () => {
    handleAccountLink(account, accountSummary?.product?.clientId, windowWidth)
  }

  const handlePromotionsClick = () => {
    setSelectedAccount(accountId)
    navigate(routes.PROMOTIONS)
  }

  return {
    handleActivityClick,
    handleAutopayClick,
    handleCBRClick,
    handleCollectionClick,
    handleDetailsClick,
    handleMAPClick,
    handleRewardsClick,
    handleAccountServicesClick,
    handleStatementsClick,
    handleRegisterAccountClick,
    handleAccountClick,
    handlePromotionsClick
  }
}

export default useGetCTAHandlers
